import { useEffect, useRef } from 'react';

import {
	clearAnalyticsIdentity,
	identifyClient,
	legalEntityName,
} from '@app/services/analytics.service';
import { readLocalStorageData } from '@app/services/localStorage.service';
import { LocalStorageItems } from '@app/shared/constants/localStorageItems';
import { useAppSelector } from '@hooks/shared/reduxHooks';

/**
 * The React half of the analytics wrapper. Mounted once by <App />; the SDK
 * itself boots in `index.tsx` before the first render, so nothing here races
 * a child's mount-time event.
 *
 * Identity follows the selected client in the store, so the login redirect,
 * the client picker and the client switcher are all covered from one place.
 * The reset itself lives in the `doLogout` thunk — see below for why the
 * store alone cannot be trusted after a logout.
 */
export const useAnalytics = (): void => {
	const client = useAppSelector((state) => state.user.client);

	// Only needed to tell "logged out" from "never had a client".
	const hadClient = useRef(false);

	useEffect(() => {
		// RESET_STORE hands every slice its initial state, which `userSlice` read
		// from localStorage at page load — so after a logout the store can still
		// hold the client the page started with. localStorage is the source of
		// truth here, exactly as for RequireAuth: `doLogout` deletes both keys,
		// login and `setSelectedClient` rewrite them.
		const persisted = readLocalStorageData(LocalStorageItems.client) as {
			counterparty?: string;
		} | null;
		const isCurrent =
			!!client &&
			!!readLocalStorageData(LocalStorageItems.isAuth) &&
			persisted?.counterparty === client.counterparty;

		if (client && isCurrent) {
			identifyClient(client.counterparty, legalEntityName(client));
		} else if (hadClient.current) {
			// Belt and braces: `doLogout` already resets. This covers a client that
			// is cleared without a logout.
			clearAnalyticsIdentity();
		}

		hadClient.current = !!client && isCurrent;
	}, [client]);
};
